"use client";

import { useEffect, useState } from "react";
import {
  getActiveSchool,
  getStudentsForActiveSchoolFromStore,
  setActiveSchoolId,
} from "../../services/backend/api";
import type { HardcodedStudent } from "../../services/backend/api";
import { useProfileContext } from "../../context/ProfileContext";
import LeftComponent from "../home/_components/LeftComponent";
import { X } from "lucide-react";

type SectionName = "A" | "B" | "C" | "D" | "E";
const SECTION_NAMES: SectionName[] = ["A", "B", "C", "D", "E"];

const StudentSignIn = () => {
  const { handleProfileSelect, logout } = useProfileContext();

  // Read the school that was set on the login page
  const activeSchool = getActiveSchool();

  const [selectedClass, setSelectedClass] = useState<number | "">("");
  const [selectedSection, setSelectedSection] = useState<SectionName | "">("");
  const [rollNo, setRollNo]               = useState("");
  const [selectedName, setSelectedName]   = useState("");
  const [error, setError]                 = useState("");
  const [students, setStudents]           = useState<HardcodedStudent[]>([]);

  useEffect(() => {
    let isMounted = true;

    const loadStudents = async () => {
      const loadedStudents = await getStudentsForActiveSchoolFromStore();
      if (isMounted) {
        setStudents(loadedStudents);
      }
    };

    loadStudents();

    return () => {
      isMounted = false;
    };
  }, []);

  // ── Derived: students in the currently-selected class ──
  const classStudents: HardcodedStudent[] =
    selectedClass
      ? students.filter((s) => s.classId === selectedClass)
      : [];

  const sectionStudents: HardcodedStudent[] =
    selectedSection
      ? classStudents.filter((s) => s.section === selectedSection)
      : [];

  // ── Handlers ──

  /** When the class changes, reset name & roll number. */
  const handleClassChange = (value: string) => {
    setSelectedClass(value ? Number(value) : "");
    setSelectedSection("");
    setRollNo("");
    setSelectedName("");
    setError("");
  };

  /** When section changes, reset name & roll number to avoid stale values. */
  const handleSectionChange = (value: string) => {
    setSelectedSection((value as SectionName) || "");
    setRollNo("");
    setSelectedName("");
    setError("");
  };

  /** Selecting a name resets roll number so the student must enter it manually. */
  const handleNameChange = (name: string) => {
    setSelectedName(name);
    setRollNo("");
    setError("");
  };

  const handleRollChange = (value: string) => {
    setRollNo(value);
    setError("");
  };

  const handleSignIn = async () => {
    setError("");

    if (!selectedClass || !selectedSection || !rollNo.trim()) {
      setError("Please fill in all fields.");
      return;
    }

    const student = students.find(
      (entry) =>
        entry.classId === Number(selectedClass) &&
        entry.section === selectedSection &&
        entry.rollNo === rollNo.trim() &&
        (!selectedName || entry.studentName === selectedName)
    );
    if (!student) {
      setError("Roll number not found. Please check and try again.");
      return;
    }
    if (selectedName && student.studentName !== selectedName) {
      setError("Roll number does not match the selected name.");
      return;
    }
    await setActiveSchoolId(activeSchool.schoolId);
    await handleProfileSelect(student);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-300 via-pink-200 to-yellow-200 overflow-hidden relative">
      <div className="container mx-auto px-4 py-8 relative z-10">

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-purple-700 mb-1">Sign In as a Student</h1>
          <p className="text-purple-500 font-medium">{activeSchool.schoolName}</p>
          <span className="text-5xl">🎒</span>
        </div>

        <div className="grid md:grid-cols-2 gap-8 items-center">
          <LeftComponent />

          <div className="bg-white/90 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/30">
            <div className="space-y-5">

              {/* Class */}
              <div>
                <label className="block text-purple-700 font-bold text-lg mb-2">Class</label>
                <select
                  value={selectedClass}
                  onChange={(e) => handleClassChange(e.target.value)}
                  className="w-full p-3 rounded-xl border-2 border-purple-300 focus:border-purple-500 focus:outline-none text-gray-700 bg-white"
                >
                  <option value="">Select Your Class</option>
                  {[1, 2, 3, 4, 5].map((grade) => (
                    <option key={grade} value={grade}>Class {grade}</option>
                  ))}
                </select>
              </div>

              {/* Section */}
              <div>
                <label className="block text-purple-700 font-bold text-lg mb-2">Section</label>
                <select
                  value={selectedSection}
                  onChange={(e) => handleSectionChange(e.target.value)}
                  disabled={!selectedClass}
                  className="w-full p-3 rounded-xl border-2 border-purple-300 focus:border-purple-500 focus:outline-none text-gray-700 bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">Select Your Section</option>
                  {SECTION_NAMES.map((section) => (
                    <option key={section} value={section}>Section {section}</option>
                  ))}
                </select>
              </div>

              {/* Name — populated once class and section are selected */}
              <div>
                <label className="block text-purple-700 font-bold text-lg mb-2">Name</label>
                <select
                  value={selectedName}
                  onChange={(e) => handleNameChange(e.target.value)}
                  disabled={!selectedClass || !selectedSection}
                  className="w-full p-3 rounded-xl border-2 border-purple-300 focus:border-purple-500 focus:outline-none text-gray-700 bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">Select Your Name</option>
                  {sectionStudents.map((s) => (
                    <option key={s.studentId} value={s.studentName}>
                      {s.studentName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Roll No. */}
              <div>
                <label className="block text-purple-700 font-bold text-lg mb-2">Roll No.</label>
                <input
                  type="number"
                  placeholder="Enter your roll number"
                  value={rollNo}
                  onChange={(e) => handleRollChange(e.target.value)}
                  className="w-full p-3 rounded-xl border-2 border-purple-300 focus:border-purple-500 focus:outline-none text-gray-700"
                />
              </div>

              {/* Inline error */}
              {error && (
                <div className="flex items-center gap-2 bg-red-100 border border-red-300 text-red-700 p-3 rounded-xl">
                  <span className="text-lg">😞</span>
                  <span className="flex-grow font-medium">{error}</span>
                  <button onClick={() => setError("")}>
                    <X className="w-5 h-5 text-red-500" />
                  </button>
                </div>
              )}

              {/* Sign In */}
              <button
                onClick={handleSignIn}
                className="w-full bg-gradient-to-r from-green-400 to-emerald-500 text-white text-xl font-bold py-4 rounded-2xl shadow-lg transform transition-all duration-300 hover:scale-105 hover:shadow-2xl cursor-pointer"
              >
                Sign In
              </button>

              {/* Back */}
              <button
                onClick={logout}
                className="w-full text-purple-600 font-medium hover:text-purple-800 transition-colors cursor-pointer"
              >
                ← Back to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentSignIn;
